import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Spinner,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "reactstrap";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";

const dataService = require("../../services/dataServices.js");

const CommentingTeamList = (props) => {
  const dateDisplay = (cell, row) => {
    if (cell !== 0) {
      return <React.Fragment>{new Date(cell).toLocaleString()}</React.Fragment>;
    } else {
      return <React.Fragment></React.Fragment>;
    }
  };

  const tableActions = (cell, row) => {
    function deleteRow() {
      setSelectedRow(row);
      setShowDeleteModal(true);
    }

    function activateRow() {
      setSelectedRow(row);
      setShowActivateModal(true);
    }

    if (props.active_flag === 1) {
      return (
        <Button color="warning" size="sm" onClick={deleteRow}>
          De-activate
        </Button>
      );
    } else {
      return (
        <Button color="success" size="sm" onClick={activateRow}>
          Activate
        </Button>
      );
    }
  };

  const nameDisplay = (cell, row) => {
    return (
      <Link to={"/analytics/commentingteam/edit/" + row.team_member_id}>
        {row.first_name} {row.last_name}
      </Link>
    );
  };

  const [currentPage, setCurrentPage] = useState(1);
  const [currentData, setCurrentData] = useState([]);
  const [showLoading, setShowLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  const [sortField, setSortField] = useState("first_name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [sizePerPage, setSizePerPage] = useState(5);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showActivateModal, setShowActivateModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  const tableColumns = [
    {
      dataField: "first_name",
      text: "Name",
      sort: true,
      formatter: nameDisplay,
      editable: false,
    },
    {
      dataField: "created_date",
      text: "Created on",
      sort: true,
      formatter: dateDisplay,
      editable: false,
    },
    {
      isDummyField: true,
      dataField: "xxx",
      text: "",
      sort: false,
      formatter: tableActions,
      headerStyle: () => {
        return { width: "120px" };
      },
    },
  ];

  function toggleDelete() {
    setShowDeleteModal(!showDeleteModal);
  }

  function toggleActivate() {
    setShowActivateModal(!showActivateModal);
  }

  async function performDelete() {
    setShowDeleteModal(false);
    setShowLoading(true);
    setCurrentData([]);

    selectedRow.active_flag = 0;

    // ok, delete the row
    await dataService.updateTeamMember(selectedRow);

    // refresh the table
    setCurrentPage(1);
    await loadData(1, sizePerPage, sortField, sortOrder);
  }

  async function performActivate() {
    setShowDeleteModal(false);
    setShowLoading(true);
    setCurrentData([]);

    selectedRow.active_flag = 1;

    // ok, delete the row
    await dataService.updateTeamMember(selectedRow);

    // refresh the table
    setCurrentPage(1);
    await loadData(1, sizePerPage, sortField, sortOrder);
  }

  async function loadData(page, pageSize, sort, order) {
    setShowLoading(true);
    setCurrentData([]);

    var info = await dataService.teamMemberList(
      props.active_flag,
      page,
      pageSize,
      sort,
      order
    );
    setTotalRecords(info.record_count);
    setShowLoading(false);
    setCurrentData(info.list);
  }

  function pageChanged(type, newState) {
    if (type === "pagination") {
      if (newState.sizePerPage !== sizePerPage) {
        // normally, we will need to make a call out
        setCurrentPage(1);
        setSizePerPage(newState.sizePerPage);
        loadData(1, newState.sizePerPage, sortField, sortOrder);
      } else if (newState.page !== currentPage) {
        // normally, we will need to make a call out to get the passed page of data
        setCurrentPage(newState.page);
        loadData(newState.page, sizePerPage, sortField, sortOrder);
      }
    } else if (type === "sort") {
      // normally, we will need to make a call out

      if (newState.sortField !== sortField) {
        setSortField(newState.sortField);
      } else if (newState.sortOrder !== sortOrder) {
        setSortOrder(newState.sortOrder);
      }

      setCurrentPage(1);
      loadData(1, sizePerPage, newState.sortField, newState.sortOrder);
    }
  }

  useEffect(() => {
    async function loadData() {
      // reset
      setCurrentPage(1);
      setSizePerPage(5);
      setSortField("first_name");
      setSortOrder("asc");

      // show spinner
      setShowLoading(true);
      setCurrentData([]);

      // load data
      var info = await dataService.teamMemberList(
        props.active_flag,
        1,
        5,
        "first_name",
        "asc"
      );
      setTotalRecords(info.record_count);
      setShowLoading(false);
      setCurrentData(info.list);
    }

    loadData();
  }, [props.active_flag]);

  return (
    <React.Fragment>
      <BootstrapTable
        keyField="team_member_id"
        data={currentData}
        columns={tableColumns}
        sort={{ dataField: sortField, order: sortOrder }}
        bootstrap4
        striped
        wrapperClasses="table_wrapper"
        classes="table_class"
        remote={{ pagination: true, filter: true, sort: true }}
        bordered={false}
        pagination={paginationFactory({
          sizePerPage: sizePerPage,
          sizePerPageList: [5, 10, 25, 50],
          page: currentPage,
          totalSize: totalRecords,
        })}
        onTableChange={pageChanged}
        noDataIndication={() =>
          showLoading ? (
            <div
              style={{ height: 250 }}
              className="d-flex align-items-center justify-content-center"
            >
              <Spinner color="primary" />
            </div>
          ) : (
            <div
              style={{ height: 250 }}
              className="d-flex align-items-center justify-content-center"
            >
              <span>There are no records</span>
            </div>
          )
        }
      />
      <Modal
        key={"delete"}
        isOpen={showDeleteModal}
        toggle={() => toggleDelete()}
      >
        <ModalHeader toggle={() => toggleDelete()}>
          De-activate Team Member
          {selectedRow
            ? " - " + selectedRow.first_name + " " + selectedRow.last_name
            : ""}
        </ModalHeader>
        <ModalBody className="text-center m-3">
          <p className="mb-0">
            Are you sure you want to de-activate this team member?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => toggleDelete()}>
            Cancel
          </Button>{" "}
          <Button color="warning" onClick={() => performDelete()}>
            De-activate
          </Button>
        </ModalFooter>
      </Modal>
      <Modal
        key={"delete"}
        isOpen={showActivateModal}
        toggle={() => toggleActivate()}
      >
        <ModalHeader toggle={() => toggleActivate()}>
          Activate Team Member
          {selectedRow
            ? " - " + selectedRow.first_name + " " + selectedRow.last_name
            : ""}
        </ModalHeader>
        <ModalBody className="text-center m-3">
          <p className="mb-0">
            Are you sure you want to activate this team member?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => toggleActivate()}>
            Cancel
          </Button>{" "}
          <Button color="success" onClick={() => performActivate()}>
            Activate
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default CommentingTeamList;
